import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  Loader2,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  PawPrint,
  Users,
  Clock,
  StickyNote,
  Lock,
  Save,
  Key,
  AlertOctagon,
  Hammer,
  Loader2 as Spin,
  CreditCard,
  DollarSign,
  CalendarClock,
  History,
  Repeat,
} from 'lucide-react';
import { ANIMAL_LABELS, SEX_LABELS } from '../lib/animals';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import BookingDocuments from './BookingDocuments';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Full booking detail for providers.
 * Shows: client, location, service, all selected animals, group quantity,
 * client booking notes, and provider private notes (editable).
 */
export default function BookingDetailModal({ bookingId, open, onOpenChange, onUpdated }) {
  const { token } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [privateNotes, setPrivateNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  useEffect(() => {
    if (!open || !bookingId) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/dashboard/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setBooking(res.data);
        setPrivateNotes(res.data?.providerNotes || '');
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load booking');
      })
      .finally(() => setLoading(false));
  }, [bookingId, open, token]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.patch(
        `${API_URL}/api/dashboard/bookings/${bookingId}`,
        { providerNotes: privateNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Private notes saved');
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (!window.confirm(`Set status to ${newStatus}?`)) return;
    try {
      await axios.patch(
        `${API_URL}/api/dashboard/bookings/${bookingId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Booking ${newStatus.toLowerCase()}`);
      onUpdated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const cancelSeries = async (scope) => {
    const labels = { this: 'this appointment only', future: 'this and all future appointments', all: 'the entire series' };
    if (!window.confirm(`Cancel ${labels[scope]}?`)) return;
    try {
      await axios.post(
        `${API_URL}/api/dashboard/recurring/${booking.seriesId}/cancel?scope=${scope}&booking_id=${bookingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Recurring appointment(s) canceled');
      onUpdated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel series');
    }
  };

  const openReschedule = () => {
    if (!booking?.startDateTime) return;
    const start = new Date(booking.startDateTime);
    setNewDate(start.toISOString().slice(0, 10));
    setNewTime(start.toISOString().slice(11, 16));
    setRescheduleReason('');
    setRescheduleOpen(true);
  };

  const submitReschedule = async () => {
    if (!newDate) {
      toast.error('Pick a new date');
      return;
    }
    setRescheduling(true);
    try {
      await axios.post(
        `${API_URL}/api/bookings/${bookingId}/reschedule`,
        {
          newDate,
          newTime: newTime || null,
          reason: rescheduleReason.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Booking rescheduled');
      setRescheduleOpen(false);
      // Reload booking detail to show new time + history entry
      const res = await axios.get(`${API_URL}/api/dashboard/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBooking(res.data);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setRescheduling(false);
    }
  };

  if (!open) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-modal">
        <DialogHeader>
          <DialogTitle>Booking Detail</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !booking ? (
          <p className="text-muted-foreground p-4">Booking not found.</p>
        ) : (
          <div className="space-y-4">
            {/* Status + service */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-lg font-bold" data-testid="detail-service-name">
                  {booking.service?.name || 'Service'}
                </h2>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Clock className="h-3 w-3" />
                  {format(parseISO(booking.startDateTime), 'EEEE, MMM d, yyyy • h:mm a')}
                  {booking.service?.durationMinutes ? ` (${booking.service.durationMinutes}m)` : ''}
                </p>
              </div>
              <StatusBadge status={booking.status} />
              {booking.isRecurring && booking.seriesId && (
                <Badge variant="outline" className="ml-2 border-primary/40 text-primary" data-testid="recurring-indicator">
                  <Repeat className="h-3 w-3 mr-1" /> Recurring{booking.seriesPosition ? ` #${booking.seriesPosition}` : ''}
                </Badge>
              )}
            </div>

            {/* Client */}
            <Section icon={<User className="h-4 w-4" />} title="Client">
              <p className="font-medium" data-testid="detail-client-name">
                {booking.client?.name || '—'}
              </p>
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mt-1">
                {booking.client?.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    <a href={`tel:${booking.client.phone}`} className="hover:text-primary">
                      {booking.client.phone}
                    </a>
                  </span>
                )}
                {booking.client?.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <a href={`mailto:${booking.client.email}`} className="hover:text-primary">
                      {booking.client.email}
                    </a>
                  </span>
                )}
              </div>
              {booking.clientUserId && (
                <Badge variant="secondary" className="mt-2 text-xs">
                  Verified client account
                </Badge>
              )}
            </Section>

            {/* Location */}
            <Section icon={<MapPin className="h-4 w-4" />} title="Job Location">
              <p data-testid="detail-location">{booking.jobLocation || booking.locationAddress}</p>
              {booking.locationNotes && (
                <p className="text-sm text-muted-foreground mt-1">{booking.locationNotes}</p>
              )}
            </Section>

            {/* Service detail */}
            <Section icon={<Briefcase className="h-4 w-4" />} title="Service">
              <p>{booking.service?.name}</p>
              {booking.service?.description && (
                <p className="text-sm text-muted-foreground mt-1">{booking.service.description}</p>
              )}
            </Section>

            {/* Riders (group services) */}
            {booking.riderCount != null && (
              <Section icon={<Users className="h-4 w-4" />} title={`Riders (${booking.riderCount})`}>
                <p className="text-sm text-muted-foreground" data-testid="detail-rider-count">{booking.riderCount} rider{booking.riderCount === 1 ? '' : 's'} in this group booking</p>
              </Section>
            )}

            {/* Documents (RW-BUILD-014) */}
            <BookingDocuments bookingId={bookingId} role="provider" />

            {/* Animals */}
            <Section
              icon={booking.animals?.length > 0 ? <PawPrint className="h-4 w-4" /> : <Users className="h-4 w-4" />}
              title={`Animals (${booking.horseCount || 0})`}
            >
              {booking.animals && booking.animals.length > 0 ? (
                <div className="space-y-2" data-testid="detail-animals">
                  {booking.animals.map((a) => (
                    <Card key={a.id} className="bg-muted/30">
                      <CardContent className="pt-3 pb-3">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-medium">{a.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {ANIMAL_LABELS[a.animalType] || a.animalType}
                          </Badge>
                          {a.sex && (
                            <Badge variant="outline" className="text-xs">
                              {SEX_LABELS[a.sex] || a.sex}
                            </Badge>
                          )}
                          {a.breed && <span className="text-xs text-muted-foreground">{a.breed}</span>}
                          {a.age != null && (
                            <span className="text-xs text-muted-foreground">• {a.age}y</span>
                          )}
                        </div>
                        {(a.notes || a.handlingNotes || a.behaviorNotes || a.medicalNotes) && (
                          <div className="text-xs text-muted-foreground space-y-0.5 mt-1">
                            {a.handlingNotes && (
                              <p>
                                <strong>Handling:</strong> {a.handlingNotes}
                              </p>
                            )}
                            {a.behaviorNotes && (
                              <p>
                                <strong>Behavior:</strong> {a.behaviorNotes}
                              </p>
                            )}
                            {a.medicalNotes && (
                              <p>
                                <strong>Medical:</strong> {a.medicalNotes}
                              </p>
                            )}
                            {a.notes && (
                              <p>
                                <strong>Notes:</strong> {a.notes}
                              </p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : booking.animalQuantity && booking.animalType ? (
                <div data-testid="detail-group">
                  <p>
                    <strong>{booking.animalQuantity}</strong>{' '}
                    {ANIMAL_LABELS[booking.animalType] || booking.animalType}
                    {booking.animalQuantity > 1 ? 's' : ''}
                  </p>
                  {booking.groupDescription && (
                    <p className="text-sm text-muted-foreground mt-1 italic">
                      {booking.groupDescription}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {booking.horseCount || 0} animal{booking.horseCount === 1 ? '' : 's'} (no detail
                  records)
                </p>
              )}
            </Section>

            {/* Client booking notes */}
            {booking.notes && (
              <Section icon={<StickyNote className="h-4 w-4" />} title="Client booking notes">
                <p className="whitespace-pre-wrap text-sm">{booking.notes}</p>
              </Section>
            )}

            {/* Provider private notes (booking-specific) */}
            <Section icon={<Lock className="h-4 w-4" />} title="Booking notes (private)">
              <Textarea
                rows={3}
                value={privateNotes}
                onChange={(e) => setPrivateNotes(e.target.value)}
                placeholder="What happened today, work completed, payment comments…"
                maxLength={5000}
                data-testid="detail-private-notes"
              />
              <Button
                size="sm"
                onClick={saveNotes}
                disabled={savingNotes || privateNotes === (booking.providerNotes || '')}
                className="mt-2"
                data-testid="detail-save-notes"
              >
                {savingNotes ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save Notes
              </Button>
            </Section>

            {/* Payment tracking (tracking-only — no payment processing) */}
            <PaymentTrackingSection booking={booking} onSaved={() => { /* parent reload */ onUpdated?.(); }} />

            {/* Private client notes (provider-private CRM about this client) */}
            {booking.client?.id && (
              <PrivateClientNotesSection clientId={booking.client.id} />
            )}

            {/* Private animal notes per selected animal */}
            {(booking.animals || []).map((a) => (
              <PrivateAnimalNotesSection
                key={a.id}
                animal={a}
                initial={booking.privateAnimalNotes?.[a.id]}
              />
            ))}

            {/* Status actions */}
            <div className="flex flex-wrap gap-2 pt-3 border-t">
              {booking.status === 'PENDING' && (
                <>
                  <Button onClick={() => updateStatus('CONFIRMED')} data-testid="confirm-btn">
                    Confirm Booking
                  </Button>
                  <Button variant="outline" onClick={() => updateStatus('CANCELED')}>
                    Decline
                  </Button>
                </>
              )}
              {booking.status === 'CONFIRMED' && (
                <>
                  <Button onClick={() => updateStatus('COMPLETED')}>Mark Completed</Button>
                  <Button variant="outline" onClick={() => updateStatus('CANCELED')}>
                    Cancel
                  </Button>
                </>
              )}
              {/* Reschedule available unless terminal status */}
              {!['CANCELED', 'COMPLETED'].includes(booking.status) && (
                <Button
                  variant="outline"
                  onClick={openReschedule}
                  data-testid="reschedule-btn"
                >
                  <CalendarClock className="h-4 w-4 mr-1" /> Reschedule
                </Button>
              )}
            </div>

            {/* RW-BUILD-003 — Recurring series management */}
            {booking.isRecurring && booking.seriesId && !['CANCELED', 'COMPLETED'].includes(booking.status) && (
              <div className="pt-3 border-t" data-testid="series-management">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Repeat className="h-3 w-3" /> Recurring series
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => cancelSeries('this')} data-testid="cancel-this-btn">
                    Cancel this only
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => cancelSeries('future')} data-testid="cancel-future-btn">
                    Cancel this &amp; future
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => cancelSeries('all')} data-testid="cancel-series-btn">
                    Cancel entire series
                  </Button>
                </div>
              </div>
            )}
            {booking.rescheduleHistory && booking.rescheduleHistory.length > 0 && (
              <div className="border-l-2 border-blue-300 pl-3 space-y-1" data-testid="reschedule-history">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1 uppercase tracking-wide">
                  <History className="h-3 w-3" /> Reschedule History
                </p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {booking.rescheduleHistory.slice().reverse().map((h) => (
                    <li key={`${h.rescheduledAt}-${h.previousStartDateTime || ''}`} className="border rounded p-2 bg-muted/30">
                      <p>
                        <strong>{format(parseISO(h.rescheduledAt), 'MMM d, yyyy h:mm a')}</strong>
                        {h.rescheduledByRole ? ` · by ${h.rescheduledByRole}` : ''}
                      </p>
                      <p>
                        <span className="line-through">
                          {h.previousStart ? format(parseISO(h.previousStart), 'MMM d, h:mm a') : '—'}
                        </span>
                        {' → '}
                        <strong className="text-foreground">
                          {h.newStart ? format(parseISO(h.newStart), 'MMM d, h:mm a') : '—'}
                        </strong>
                      </p>
                      {h.reason && <p className="italic">"{h.reason}"</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>

      {/* Reschedule Dialog (sibling — avoids nested-dialog overlay issues) */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="max-w-md" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule Booking</DialogTitle>
          </DialogHeader>
          {booking?.startDateTime && (
            <p className="text-sm text-muted-foreground">
              Currently: <strong>{format(parseISO(booking.startDateTime), 'EEE MMM d, yyyy h:mm a')}</strong>
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">New date</label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                data-testid="reschedule-date"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">New time (optional)</label>
              <Input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                data-testid="reschedule-time"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Reason (optional)</label>
            <Textarea
              rows={2}
              value={rescheduleReason}
              onChange={(e) => setRescheduleReason(e.target.value)}
              placeholder="Why moving this booking? Client request, weather, scheduling conflict…"
              maxLength={500}
              data-testid="reschedule-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitReschedule}
              disabled={rescheduling || !newDate}
              data-testid="reschedule-save"
            >
              {rescheduling ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <CalendarClock className="h-4 w-4 mr-1" />
              )}
              Move Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="border-l-2 border-primary/30 pl-3">
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1 uppercase tracking-wide">
        {icon} {title}
      </p>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const color =
    status === 'PENDING'
      ? 'bg-yellow-100 text-yellow-800'
      : status === 'CONFIRMED'
      ? 'bg-blue-100 text-blue-800'
      : status === 'COMPLETED'
      ? 'bg-green-100 text-green-800'
      : status === 'CANCELED'
      ? 'bg-red-100 text-red-700'
      : 'bg-gray-100 text-gray-700';
  return <Badge className={color}>{status}</Badge>;
}

function fmtMoney(cents) {
  const v = (cents || 0) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

const PAYMENT_BADGE = {
  PAID: 'bg-green-100 text-green-800 border-green-300',
  PARTIAL: 'bg-amber-100 text-amber-800 border-amber-300',
  UNPAID: 'bg-red-100 text-red-700 border-red-300',
};

function PaymentTrackingSection({ booking, onSaved }) {
  const { token } = useAuth();
  const [amountDue, setAmountDue] = React.useState(
    booking.amountDueCents != null ? (booking.amountDueCents / 100).toFixed(2) : ''
  );
  const [amountPaid, setAmountPaid] = React.useState(
    booking.amountPaidCents != null ? (booking.amountPaidCents / 100).toFixed(2) : ''
  );
  const [paymentMethod, setPaymentMethod] = React.useState(booking.paymentMethod || '');
  const [paymentNotes, setPaymentNotes] = React.useState(booking.paymentNotes || '');
  const [saving, setSaving] = React.useState(false);

  const dueCents = Math.round((parseFloat(amountDue) || 0) * 100);
  const paidCents = Math.round((parseFloat(amountPaid) || 0) * 100);
  const balanceCents = Math.max(0, dueCents - paidCents);
  const derivedStatus =
    dueCents > 0 && paidCents >= dueCents ? 'PAID' : paidCents > 0 ? 'PARTIAL' : 'UNPAID';

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/dashboard/bookings/${booking.id}`,
        {
          amountDueCents: dueCents,
          amountPaidCents: paidCents,
          paymentMethod: paymentMethod.trim() || null,
          paymentNotes: paymentNotes.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payment updated');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-l-2 border-emerald-300 pl-3 space-y-2" data-testid="payment-tracking-section">
      <div className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1 uppercase tracking-wide">
        <CreditCard className="h-3 w-3" /> Payment tracking
        <Badge className={`${PAYMENT_BADGE[derivedStatus]} border ml-2 normal-case text-xs`}>
          {derivedStatus}
        </Badge>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-xs text-muted-foreground">Amount due</label>
          <div className="relative">
            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="number"
              min={0}
              step="0.01"
              value={amountDue}
              onChange={(e) => setAmountDue(e.target.value)}
              placeholder="0.00"
              className="pl-7"
              data-testid="payment-due-input"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Amount paid</label>
          <div className="relative">
            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="number"
              min={0}
              step="0.01"
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
              placeholder="0.00"
              className="pl-7"
              data-testid="payment-paid-input"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Balance due</label>
          <div className="h-10 px-3 flex items-center font-semibold border rounded bg-muted/40" data-testid="payment-balance">
            {fmtMoney(balanceCents)}
          </div>
        </div>
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Payment method</label>
        <Input
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          placeholder="Cash, Check, Venmo, Zelle…"
          maxLength={100}
          data-testid="payment-method-input"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Payment notes</label>
        <Textarea
          rows={2}
          value={paymentNotes}
          onChange={(e) => setPaymentNotes(e.target.value)}
          placeholder="Check #1042, sent invoice, owner promised next week…"
          maxLength={2000}
          data-testid="payment-notes-input"
        />
      </div>
      <Button size="sm" onClick={save} disabled={saving} data-testid="payment-save-btn">
        {saving ? <Spin className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
        Save Payment
      </Button>
      <p className="text-[10px] text-muted-foreground italic">
        Tracking only — RanchWorks does not process card payments yet.
      </p>
    </div>
  );
}

function PrivateClientNotesSection({ clientId }) {
  const { token } = useAuth();
  const [notes, setNotes] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/dashboard/private-client-notes/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setNotes(r.data || {}))
      .catch(() => setNotes({}))
      .finally(() => setLoading(false));
  }, [clientId, token]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        gateCode: notes.gateCode || null,
        parkingInstructions: notes.parkingInstructions || null,
        preferredContactMethod: notes.preferredContactMethod || null,
        paymentNotes: notes.paymentNotes || null,
        difficultyFlag: !!notes.difficultyFlag,
        generalNotes: notes.generalNotes || null,
      };
      const res = await axios.put(
        `${API_URL}/api/dashboard/private-client-notes/${clientId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes(res.data || notes);
      toast.success('Private client notes saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !notes) {
    return (
      <div className="border-l-2 border-amber-300 pl-3">
        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1 uppercase tracking-wide">
          <Key className="h-3 w-3" /> Private client notes
        </p>
        <Spin className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const set = (k) => (e) => {
    const v = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setNotes((n) => ({ ...n, [k]: v }));
  };

  return (
    <div className="border-l-2 border-amber-300 pl-3 space-y-2">
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1 uppercase tracking-wide">
        <Key className="h-3 w-3" /> Private client notes
        <span className="ml-1 text-[10px] font-normal text-amber-700 normal-case tracking-normal">
          (only you can see this)
        </span>
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-muted-foreground">Gate code</label>
          <Input
            value={notes.gateCode || ''}
            onChange={set('gateCode')}
            placeholder="e.g. 4-2-7-9"
            maxLength={200}
            data-testid="pcn-gate-code"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Preferred contact</label>
          <Input
            value={notes.preferredContactMethod || ''}
            onChange={set('preferredContactMethod')}
            placeholder="Phone / Text / Email"
            maxLength={50}
          />
        </div>
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Parking instructions</label>
        <Input
          value={notes.parkingInstructions || ''}
          onChange={set('parkingInstructions')}
          placeholder="Where to park, road conditions…"
          maxLength={1000}
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Payment notes</label>
        <Input
          value={notes.paymentNotes || ''}
          onChange={set('paymentNotes')}
          placeholder="e.g. Pays cash, often late"
          maxLength={1000}
        />
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox
          checked={!!notes.difficultyFlag}
          onCheckedChange={(v) => set('difficultyFlag')(!!v)}
          data-testid="pcn-difficulty"
        />
        <span>Difficult owner — handle with care</span>
      </label>
      <Textarea
        rows={2}
        value={notes.generalNotes || ''}
        onChange={set('generalNotes')}
        placeholder="Anything else only you should remember…"
        maxLength={5000}
      />
      <Button size="sm" onClick={save} disabled={saving} data-testid="pcn-save">
        {saving ? <Spin className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
        Save Private Client Notes
      </Button>
    </div>
  );
}

function PrivateAnimalNotesSection({ animal, initial }) {
  const { token } = useAuth();
  const [notes, setNotes] = React.useState(
    initial || {
      kicksOnBackFeet: false,
      bites: false,
      needsStocks: false,
      requiresExtraTime: false,
      needsHelper: false,
      generalNotes: '',
    }
  );
  const [saving, setSaving] = React.useState(false);

  const toggle = (k) => setNotes((n) => ({ ...n, [k]: !n[k] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        kicksOnBackFeet: !!notes.kicksOnBackFeet,
        bites: !!notes.bites,
        needsStocks: !!notes.needsStocks,
        requiresExtraTime: !!notes.requiresExtraTime,
        needsHelper: !!notes.needsHelper,
        generalNotes: notes.generalNotes || null,
      };
      const res = await axios.put(
        `${API_URL}/api/dashboard/private-animal-notes/${animal.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes(res.data || notes);
      toast.success(`Private notes saved for ${animal.name}`);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const flags = [
    { k: 'kicksOnBackFeet', label: 'Kicks on back feet' },
    { k: 'bites', label: 'Bites' },
    { k: 'needsStocks', label: 'Needs stocks' },
    { k: 'requiresExtraTime', label: 'Requires extra time' },
    { k: 'needsHelper', label: 'Bring a helper' },
  ];

  return (
    <div className="border-l-2 border-rose-300 pl-3 space-y-2">
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1 uppercase tracking-wide">
        <AlertOctagon className="h-3 w-3" />
        Private notes — {animal.name}
        <span className="ml-1 text-[10px] font-normal text-rose-700 normal-case tracking-normal">
          (only you can see this)
        </span>
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {flags.map((f) => (
          <label
            key={f.k}
            className="flex items-center gap-2 text-sm p-1.5 rounded hover:bg-muted cursor-pointer"
          >
            <Checkbox
              checked={!!notes[f.k]}
              onCheckedChange={() => toggle(f.k)}
              data-testid={`pan-${f.k}-${animal.id}`}
            />
            <span>{f.label}</span>
          </label>
        ))}
      </div>
      <Textarea
        rows={2}
        value={notes.generalNotes || ''}
        onChange={(e) => setNotes((n) => ({ ...n, generalNotes: e.target.value }))}
        placeholder="Anything else about this animal you want to remember…"
        maxLength={5000}
      />
      <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid={`pan-save-${animal.id}`}>
        {saving ? <Spin className="h-4 w-4 animate-spin mr-1" /> : <Hammer className="h-4 w-4 mr-1" />}
        Save Private Animal Notes
      </Button>
    </div>
  );
}
